import { useState } from 'react';
import { Dropdown } from 'antd';

import { colorConstants } from 'config/colorConstants';
import { ppaSimTableKpiGroupHeaderTestId, ppaSimTableKpiSortBtnTestId } from 'constants/ppaTestIds';

import KpiSortingPanel, {
    DEFAULT_KPI_SORT_OPTIONS,
    type KpiSortOption,
    type KpiSortingValue,
} from './KpiSortingPanel';

type KpiColumnGroupHeaderProps = {
    groupKey: string;
    title: string;
    options?: KpiSortOption[];
    value: KpiSortingValue;
    isApplied?: boolean;
    onApply: (value: KpiSortingValue) => void;
};

const KPI_SORT_DROPDOWN_OVERLAY_STYLE = {
    padding: 0,
    background: 'transparent',
    boxShadow: 'none',
};

const KpiSortSettingsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
        <path
            d="M2 4C2.93192 4 3.71499 4.63738 3.93701 5.5H9.5C9.77614 5.5 10 5.72386 10 6C10 6.27614 9.77614 6.5 9.5 6.5H3.93701C3.71499 7.36262 2.93192 8 2 8C0.89543 8 0 7.10457 0 6C0 4.89543 0.89543 4 2 4ZM9 2C9 1.44772 8.55228 1 8 1C7.44772 1 7 1.44772 7 2C7 2.55228 7.44772 3 8 3C8.55228 3 9 2.55228 9 2ZM1 6C1 6.55228 1.44772 7 2 7C2.55228 7 3 6.55228 3 6C3 5.44772 2.55228 5 2 5C1.44772 5 1 5.44772 1 6ZM10 2C10 3.10457 9.10457 4 8 4C7.06808 4 6.28501 3.36262 6.06299 2.5H0.5C0.223858 2.5 0 2.27614 0 2C2.98023e-08 1.72386 0.223858 1.5 0.5 1.5H6.06299C6.28501 0.637384 7.06808 0 8 0C9.10457 0 10 0.89543 10 2Z"
            fill={colorConstants.tableSortIconPurple}
        />
    </svg>
);

const KpiColumnGroupHeader = ({
    groupKey,
    title,
    options = DEFAULT_KPI_SORT_OPTIONS,
    value,
    isApplied = false,
    onApply,
}: KpiColumnGroupHeaderProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const showSort = options.length > 0;

    const handleCancel = () => {
        setIsOpen(false);
    };

    const handleApply = (nextValue: KpiSortingValue) => {
        onApply(nextValue);
        setIsOpen(false);
    };

    return (
        <div className="flex w-full items-center justify-between gap-2" data-testid={ppaSimTableKpiGroupHeaderTestId(groupKey)}>
            <span className="truncate text-[12px] font-semibold leading-[18px]">{title}</span>
            {showSort ? (
            <Dropdown
                trigger={['click']}
                open={isOpen}
                onOpenChange={setIsOpen}
                placement="bottomRight"
                overlayClassName="kpi-sorting-dropdown"
                overlayStyle={KPI_SORT_DROPDOWN_OVERLAY_STYLE}
                destroyPopupOnHide
                getPopupContainer={() => document.body}
                dropdownRender={() => (
                    <KpiSortingPanel
                        groupKey={groupKey}
                        options={options}
                        value={value}
                        onCancel={handleCancel}
                        onApply={handleApply}
                    />
                )}
            >
                <div
                    className={`kpi-sort-trigger inline-flex size-4 shrink-0 items-center justify-center rounded ${
                        isOpen ? 'is-open' : '' }${isApplied ? ' is-applied' : ''}`}
                    data-testid={ppaSimTableKpiSortBtnTestId(groupKey)}
                >
                    <KpiSortSettingsIcon />
                </div>
            </Dropdown>
            ) : null}
        </div>
    );
};

export default KpiColumnGroupHeader;
